import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { Button, DatePicker } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

type ClassTypeItem = { id: string; name: string };
type LevelItem = { id: string; name: string };
type InstructorItem = { id: string; name: string };

export type AttendanceReportSearch = {
  dateFrom?: string;
  dateTo?: string;
  classTypeId?: string;
  levelId?: string;
  instructorId?: string;
};

export function AttendanceReportFilter() {
  const nav = useNavigate({ from: "/app/reports/attendance" });
  const search = useSearch({
    from: "/app/reports/attendance",
  }) as AttendanceReportSearch;

  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");

  const [fromDate, setFromDate] = useState(search.dateFrom ?? defaultFrom);
  const [toDate, setToDate] = useState(search.dateTo ?? defaultTo);
  const [classTypeId, setClassTypeId] = useState(search.classTypeId ?? "");
  const [levelId, setLevelId] = useState(search.levelId ?? "");
  const [instructorId, setInstructorId] = useState(search.instructorId ?? "");

  useEffect(() => {
    setFromDate(search.dateFrom ?? defaultFrom);
    setToDate(search.dateTo ?? defaultTo);
    setClassTypeId(search.classTypeId ?? "");
    setLevelId(search.levelId ?? "");
    setInstructorId(search.instructorId ?? "");
  }, [
    search.dateFrom,
    search.dateTo,
    search.classTypeId,
    search.levelId,
    search.instructorId,
  ]);

  const hasFilter =
    classTypeId || levelId || instructorId || search.dateFrom || search.dateTo;

  const applyFilters = (updates: Partial<AttendanceReportSearch>) => {
    const next = {
      dateFrom: updates.dateFrom ?? fromDate,
      dateTo: updates.dateTo ?? toDate,
      classTypeId:
        updates.classTypeId !== undefined
          ? updates.classTypeId
          : classTypeId
            ? classTypeId
            : undefined,
      levelId:
        updates.levelId !== undefined
          ? updates.levelId
          : levelId
            ? levelId
            : undefined,
      instructorId:
        updates.instructorId !== undefined
          ? updates.instructorId
          : instructorId
            ? instructorId
            : undefined,
    };
    nav({ search: next });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <DatePicker
            mode="range"
            value={{
              from: fromDate ? new Date(fromDate) : undefined,
              to: toDate ? new Date(toDate) : undefined,
            }}
            onChange={(range) => {
              const from: Date | undefined = Array.isArray(range)
                ? range[0]
                : (range as { from?: Date; to?: Date })?.from;
              const to: Date | undefined = Array.isArray(range)
                ? range[1]
                : (range as { from?: Date; to?: Date })?.to;
              const fromStr = from ? format(from, "yyyy-MM-dd") : defaultFrom;
              const toStr = to ? format(to, "yyyy-MM-dd") : defaultTo;
              setFromDate(fromStr);
              setToDate(toStr);
              applyFilters({ dateFrom: fromStr, dateTo: toStr });
            }}
            placeholder="ຊ່ວງວັນທີ"
            className="h-8 w-full sm:w-fit"
          />

          <InfiniteCombobox<ClassTypeItem>
            queryKey={["master", "class-types", "lookup"]}
            queryFn={({ search: q, pageParam }) =>
              fetchLookupForInfinite<ClassTypeItem>(
                `${config.apiUrl}/master/class-types/lookup`,
                { search: q, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<ClassTypeItem>(
                `${config.apiUrl}/master/class-types/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={classTypeId}
            onValueChange={(val) => {
              setClassTypeId(val);
              applyFilters({ classTypeId: val || undefined });
            }}
            placeholder="ປະເພດຄລາດສ໌"
            className="h-8 w-full sm:w-40"
            clearable
          />

          <InfiniteCombobox<LevelItem>
            queryKey={["master", "levels", "lookup"]}
            queryFn={({ search: q, pageParam }) =>
              fetchLookupForInfinite<LevelItem>(
                `${config.apiUrl}/master/levels/lookup`,
                { search: q, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<LevelItem>(
                `${config.apiUrl}/master/levels/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={levelId}
            onValueChange={(val) => {
              setLevelId(val);
              applyFilters({ levelId: val || undefined });
            }}
            placeholder="ລະດັບ"
            className="h-8 w-full sm:w-40"
            clearable
          />

          <InfiniteCombobox<InstructorItem>
            queryKey={["master", "instructors", "lookup"]}
            queryFn={({ search: q, pageParam }) =>
              fetchLookupForInfinite<InstructorItem>(
                `${config.apiUrl}/master/instructors/lookup`,
                { search: q, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<InstructorItem>(
                `${config.apiUrl}/master/instructors/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={instructorId}
            onValueChange={(val) => {
              setInstructorId(val);
              applyFilters({ instructorId: val || undefined });
            }}
            placeholder="ຄູສອນ"
            className="h-8 w-full sm:w-40"
            clearable
          />
        </div>

        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setClassTypeId("");
              setLevelId("");
              setInstructorId("");
              setFromDate(defaultFrom);
              setToDate(defaultTo);
              nav({
                search: {
                  dateFrom: defaultFrom,
                  dateTo: defaultTo,
                },
              });
            }}
            className="max-sm:w-full"
          >
            <XIcon className="size-4" />
            ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
